<template>
  <div class="responsive-image" :class="orientation" :style="wrapperStyles">
    <div :class="containerClassNames" :style="containerStyles">
      <img
        v-if="image"
        v-once
        :src="largestSrc"
        :srcset="lqip"
        :data-srcset="srcset"
        data-sizes="auto"
        :width="image.width"
        :height="image.height"
        :alt="alt || image.caption"
        :class="imgClassNames"
        :style="imgStyles"
      />
    </div>
  </div>
</template>

<script>
import { imageUrlFor } from '@/sanity'

export default {
  name: 'ResponsiveImage',
  props: {
    image: {
      type: Object,
      required: true
    },
    preload: {
      type: Boolean,
      default: false
    },
    ratio: {
      type: Boolean,
      default: true
    },
    alt: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      empty:
        'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==',
      sizes: [300, 600, 900]
    }
  },
  computed: {
    dimensions() {
      const matches = this.image.asset._ref.match(/-(\d+)x(\d+)-(\w+)$/)
      return {
        width: Number(matches[1]),
        height: Number(matches[2])
      }
    },
    width() {
      return this.dimensions.width
    },
    height() {
      return this.dimensions.height
    },
    aspect() {
      return this.width / this.height
    },
    sources() {
      return this.sizes.map((width) => {
        const height = Math.round(width * this.aspect)
        const url = imageUrlFor(this.image)
          .ignoreImageParams()
          .width(width)
          .fit('max')
          .auto('format')
          .quality(80)
        return { width, height, url }
      })
    },
    urls() {
      return this.sources.map(({ url }) => url)
    },
    largestSrc() {
      return this.urls.slice(-1).pop()
    },
    srcset() {
      const sources = this.sources.map(
        (src) => `${src.url} ${src.width}w ${src.height}h`
      )
      return sources.join(', ')
    },
    lqip() {
      return this.image.lqip || this.empty
    },
    objectPosition() {
      if (this.image.focus) {
        const left = (this.image.focus.x * 100).toFixed(2)
        const top = (this.image.focus.y * 100).toFixed(2)
        return `${left}% ${top}%`
      }
      return false
    },
    aspectRatioPadding() {
      const padding = ((1 / this.aspect) * 100).toFixed(2)
      return `${padding}%`
    },
    orientation() {
      if (!this.aspect) {
        return ''
      }
      if (this.aspect > 1.1) {
        return 'landscape'
      }
      if (this.aspect < 1.1) {
        return 'portrait'
      }
      return 'square'
    },
    wrapperStyles() {
      return { '--aspect-ratio': this.aspect || 0 }
    },
    containerStyles() {
      return this.ratio ? { 'padding-bottom': this.aspectRatioPadding } : {}
    },
    containerClassNames() {
      return { 'ratio-container': this.ratio }
    },
    imgClassNames() {
      return [
        this.orientation,
        'lazyload',
        {
          lazypreload: this.preload,
          lazylqip: this.image.lqip
        }
      ]
    },
    imgStyles() {
      return this.objectPosition
        ? { 'object-position': this.objectPosition }
        : {}
    }
  },
  watch: {
    orientation: {
      immediate: true,
      handler(val) {
        this.$emit('orientation', val)
      }
    },
    aspect: {
      immediate: true,
      handler(val) {
        this.$emit('aspect', val)
      }
    }
  }
}
</script>

<style scoped>
.ratio-container {
  position: relative;
  max-width: 100%;
  height: 0;
  overflow: hidden;
}
.ratio-container img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>
